import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BlogCard from '../components/BlogCard';
import { calculateTokensWithAI } from '../services/openaiService';
import { useSEO } from '../utils/seo';
import { allBlogPosts } from '../data/blog';

const TokenCalculator: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('gpt-3.5-turbo');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useSEO({
    title: 'AI Token Calculator - Test & Calculate OpenAI GPT Token Usage | Alertaio',
    description: 'Free AI token calculator to test your prompts and see exact token usage with live AI responses. Calculate costs for GPT-3.5 Turbo and GPT-4 models before making API calls.',
    keywords: 'AI token calculator, OpenAI token counter, GPT-4 token calculator, prompt testing tool, LLM token estimator, API cost calculator, ChatGPT tokens, AI cost optimization',
    canonicalUrl: 'https://alertaio.com/token-calculator',
    ogImage: 'https://alertaio.com/og-token-calculator.png',
    type: 'website'
  });

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'AI Token Calculator',
      description: 'Calculate and estimate AI API token usage with live responses from GPT models',
      applicationCategory: 'DeveloperApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: 'Token calculation, Live AI responses, Multiple model support, Cost estimation'
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleCalculate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const data = await calculateTokensWithAI(prompt, model);
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to calculate tokens');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navigation />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header with SEO-optimized content */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Free AI Token Calculator - Test GPT-4 & GPT-3.5 Turbo Token Usage
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-6">
              Calculate exact token usage for OpenAI GPT-4, GPT-3.5 Turbo models. 
              Test your prompts with live AI responses and estimate API costs before deployment.
              <span className="block mt-2 text-indigo-600 font-medium">More AI providers coming soon!</span>
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm">
              <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">
                ✓ 100% Free Tool
              </span>
              <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">
                ✓ Live AI Responses
              </span>
              <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold">
                ✓ Accurate Token Counts
              </span>
            </div>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            {/* Model Selection */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Select Model
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Faster, Cheaper)</option>
                <option value="gpt-4">GPT-4 (More Accurate, Expensive)</option>
                <option value="gpt-4-turbo-preview">GPT-4 Turbo (Best Balance)</option>
              </select>
            </div>

            {/* Prompt Input */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Your Prompt
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Enter your prompt here... (e.g., 'Explain quantum computing in simple terms')"
                rows={6}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              />
              <div className="text-sm text-slate-500 mt-2">
                Characters: {prompt.length}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Calculate Button */}
            <button
              onClick={handleCalculate}
              disabled={loading || !prompt.trim()}
              className="w-full py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Calculating...
                </span>
              ) : (
                'Calculate Tokens & Get Response'
              )}
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-6">
              {/* Token Usage Stats */}
              <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl shadow-xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-6">Token Usage</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-sm text-blue-100 mb-1">Prompt Tokens</div>
                    <div className="text-3xl font-bold">{result.tokens.prompt}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-sm text-blue-100 mb-1">Response Tokens</div>
                    <div className="text-3xl font-bold">{result.tokens.completion}</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-sm text-blue-100 mb-1">Total Tokens</div>
                    <div className="text-3xl font-bold">{result.tokens.total}</div>
                  </div>
                </div>
                <div className="mt-6 text-sm text-blue-100">
                  Model: <span className="font-semibold">{result.model}</span>
                </div>
              </div>

              {/* AI Response */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">AI Response</h2>
                <div className="prose max-w-none">
                  <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
                    <p className="text-slate-700 whitespace-pre-wrap">{result.response}</p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="font-semibold text-blue-900 mb-2">💡 Why This Matters</h3>
                <p className="text-blue-800 text-sm">
                  Token usage directly impacts your API costs. By testing prompts here, you can optimize 
                  your prompts to use fewer tokens while maintaining quality. Use our{' '}
                  <a href="/cost-calculator" className="underline font-semibold">Cost Calculator</a>{' '}
                  to estimate exact costs based on these token counts.
                </p>
              </div>
            </div>
          )}

          {/* SEO Content Section */}
          <div className="mt-12 prose prose-lg max-w-none">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Understanding AI Tokens and API Costs
              </h2>
              
              <div className="space-y-6 text-slate-700">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    What Are AI Tokens?
                  </h3>
                  <p className="leading-relaxed">
                    AI tokens are the fundamental units that large language models (LLMs) use to process text. 
                    When you send a prompt to OpenAI GPT-4, Claude, or any other AI model, your text is broken 
                    down into tokens. Understanding token usage is crucial for managing API costs effectively.
                  </p>
                  <ul className="mt-3 space-y-2 list-disc list-inside">
                    <li>1 token ≈ 4 characters in English text</li>
                    <li>1 token ≈ 0.75 words on average</li>
                    <li>1,000 tokens ≈ 750 words or about 2-3 paragraphs</li>
                    <li>Most models charge separately for input and output tokens</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    How to Use Our Token Calculator
                  </h3>
                  <p className="leading-relaxed">
                    Our free AI token calculator helps you test prompts before making expensive API calls:
                  </p>
                  <ol className="mt-3 space-y-2 list-decimal list-inside">
                    <li><strong>Select your model:</strong> Choose between GPT-3.5 Turbo (cost-effective), GPT-4 (most accurate), or GPT-4 Turbo (balanced)</li>
                    <li><strong>Enter your prompt:</strong> Type or paste the text you want to test</li>
                    <li><strong>Get instant results:</strong> See exact token counts and live AI response</li>
                    <li><strong>Estimate costs:</strong> Use token counts with our cost calculator to predict expenses</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    Why Token Calculation Matters for Developers
                  </h3>
                  <p className="leading-relaxed">
                    For developers and businesses using AI APIs, token usage directly impacts costs. A single 
                    inefficient prompt can waste thousands of tokens daily. Our token calculator helps you:
                  </p>
                  <ul className="mt-3 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span><strong>Optimize prompts</strong> to reduce unnecessary token consumption</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span><strong>Test before deployment</strong> to avoid surprise bills in production</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span><strong>Compare models</strong> to find the best balance of cost and quality</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span><strong>Budget accurately</strong> for your AI-powered applications</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 rounded-r-lg">
                  <h4 className="font-bold text-indigo-900 mb-2">
                    💡 Pro Tip: Set Up Budget Alerts
                  </h4>
                  <p className="text-indigo-800 text-sm">
                    After testing your prompts, use <Link to="/register" className="underline font-semibold">Alertaio's budget monitoring</Link> to 
                    track real-time API usage and get alerts before you exceed your spending limits. Prevent 
                    unexpected bills with automated cost tracking across OpenAI, Anthropic, and other providers.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Info Section - Replace with Blog Cards */}
          <div className="mt-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Learn More About AI Tokens & Costs
              </h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Explore our comprehensive guides to master AI API pricing and optimization
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {allBlogPosts
                .filter(post => 
                  post.tags.some(tag => 
                    ['tokens', 'API', 'OpenAI', 'cost', 'optimization', 'GPT'].some(keyword => 
                      tag.toLowerCase().includes(keyword.toLowerCase())
                    )
                  ) || post.category === 'AI Development' || post.category === 'AI Cost Management'
                )
                .slice(0, 3)
                .map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
              >
                View All Articles
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TokenCalculator;
